import type {
  MultiplexUpdateFieldErrorV2WebDto,
  UserPropertyMultiplexUpdateResponseV2WebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type GetFirstUnitErrorOptions = {
  fieldName?: string;
  fieldNames?: ReadonlyArray<string>;
  fallbackMessage?: string;
  responseFailed?: boolean;
};

const matchesFieldFilter = (
  error: MultiplexUpdateFieldErrorV2WebDto,
  fieldName: string | undefined,
  fieldNames: ReadonlyArray<string> | undefined,
): boolean => {
  if (fieldName) {
    return error.fieldName === fieldName;
  }

  return fieldNames?.includes(error.fieldName) ?? false;
};

/**
 * Returns the first error message from multiplex update units, in unit order.
 * When fieldName or fieldNames is set, returns the first matching field error per unit.
 */
export const getFirstUnitError = (
  units: UserPropertyMultiplexUpdateResponseV2WebDto['units'] | undefined,
  options?: GetFirstUnitErrorOptions,
): string | undefined => {
  const { fieldName, fieldNames, fallbackMessage, responseFailed } = options ?? {};
  const hasFieldFilter = fieldName != null || (fieldNames?.length ?? 0) > 0;

  if (hasFieldFilter) {
    for (const unit of units ?? []) {
      const fieldError = unit.errors?.find((error) => matchesFieldFilter(error, fieldName, fieldNames));
      if (fieldError?.message) {
        return fieldError.message;
      }
    }
  }

  for (const unit of units ?? []) {
    const firstError = unit.errors?.[0];
    if (firstError?.message) {
      return firstError.message;
    }
  }

  const hasUnitFailure =
    responseFailed ||
    (units?.some((unit) => unit.success === false) ?? false) ||
    (units?.some((unit) => (unit.errors?.length ?? 0) > 0) ?? false);

  if (fallbackMessage && hasUnitFailure) {
    return fallbackMessage;
  }

  return undefined;
};
